/**
 * FlagBadge Component
 *
 * Displays a color-coded badge for flag priority levels
 */

import React from 'react';
import {
  ErrorOutline as CriticalIcon,
  Info as MediumIcon,
  Remove as LowIcon,
  WarningAmber as HighIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { FLAG_BG_COLORS, FLAG_COLORS } from '../../constants';
import type { FlagPriority } from '../../types';

interface FlagBadgeProps {
  /** The priority level of the flag */
  priority: FlagPriority;
  /** Optional count to display */
  count?: number;
  /** Whether to show only the icon (compact mode) */
  compact?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

const PRIORITY_ICONS: Record<FlagPriority, React.ReactNode> = {
  Critical: <CriticalIcon fontSize="inherit" />,
  High: <HighIcon fontSize="inherit" />,
  Medium: <MediumIcon fontSize="inherit" />,
  Low: <LowIcon fontSize="inherit" />,
};

export const FlagBadge: React.FC<FlagBadgeProps> = ({
  priority,
  count,
  compact = false,
  size = 'small',
}) => {
  const color = FLAG_COLORS[priority];
  const bgColor = FLAG_BG_COLORS[priority];
  const fontSize = size === 'small' ? '0.75rem' : '0.875rem';
  const iconSize = size === 'small' ? '0.875rem' : '1rem';
  const padding = size === 'small' ? '2px 6px' : '4px 8px';

  if (compact) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          bgcolor: bgColor,
          borderRadius: '50%',
          width: size === 'small' ? 20 : 24,
          height: size === 'small' ? 20 : 24,
          fontSize: iconSize,
        }}
      >
        {PRIORITY_ICONS[priority]}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        color,
        bgcolor: bgColor,
        borderRadius: 1,
        padding,
        fontSize,
        fontWeight: 500,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', fontSize: iconSize }}>
        {PRIORITY_ICONS[priority]}
      </Box>
      {count !== undefined ? (
        <Typography variant="inherit" component="span" sx={{ fontWeight: 600 }}>
          {count}
        </Typography>
      ) : (
        <Typography variant="inherit" component="span">
          {priority}
        </Typography>
      )}
    </Box>
  );
};

export default FlagBadge;

